import { bareDomainOf } from "@/lib/company-mention-reliability";
import { normalizeMarkerQuotes } from "@shaarp/common";

/**
 * Bridges the LLM company markers and Streamdown's custom-tag rendering.
 *
 * The assistant emits an inline marker for every company it cites, with or
 * without an identifier — `name` is the only attribute it cannot do without:
 *   `[@shaarp/company name="<Name>" id="<uuid>" city="<City>" siret="…"
 *    siren="…" domain="…" linkedin="…" address="…" activity="…"
 *    source="<url> <url>" brief="<why it is cited>"]`
 * A marker written before an attribute existed is still read.
 *
 * We turn each *complete* marker into a `<company>` element whose text is the
 * `name` (the displayed label), e.g.
 *   `[@shaarp/company id="u" name="Acme"]` → `<company id="u" name="Acme">Acme</company>`
 *
 * Streamdown then renders it through the `CompanyMention` component. Attribute
 * values and the label are HTML-escaped so an LLM-provided name can never
 * inject markup.
 */

// A complete marker: `[@shaarp/company <attributes>]`.
const COMPANY_MARKER_REGEX = /\[@shaarp\/company\s+([^\]]*)\]/g;

// A marker still being streamed (opened but not yet closed) at the very end of
// the string. Stripping it avoids briefly flashing the raw syntax.
const TRAILING_INCOMPLETE_MARKER_REGEX = /\[@shaarp\/company\b[^\]]*$/;

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

type CompanyMarkerAttributes = {
  id: string;
  name: string;
  domain: string;
  siren: string;
  siret: string;
  /** The company's LinkedIn page URL — see `company-mention-reliability.ts`. */
  linkedin: string;
  city: string;
  address: string;
  activity: string;
  /** One or more http(s) URLs, kept as written: `toCompanyTag` reads them. */
  source: string;
  brief: string;
};

type CompanyMarkerAttribute = keyof CompanyMarkerAttributes;

// What a `<company>` element carries besides its `name`, in the order it
// writes them.
const ELEMENT_ATTRIBUTES: readonly Exclude<CompanyMarkerAttribute, "name">[] = [
  "id",
  "domain",
  "siren",
  "siret",
  "linkedin",
  "city",
  "address",
  "activity",
  "source",
  "brief",
];

function extractAttribute(
  attributes: string,
  attribute: CompanyMarkerAttribute,
): string {
  const match = attributes.match(new RegExp(`\\b${attribute}="([^"]*)"`));
  return match?.[1]?.trim() ?? "";
}

/**
 * Reads every attribute of a raw `[@shaarp/company ...]` marker body. The quotes
 * delimiting a value are straightened first (the LLM may emit curly ones), the
 * way the API reads the marker (`normalizeMarkerQuotes`), so the HTML
 * transform and the plain-text flattening share one extraction pass.
 */
function parseMarkerAttributes(rawAttributes: string): CompanyMarkerAttributes {
  const attributes = normalizeMarkerQuotes(rawAttributes);
  return {
    id: extractAttribute(attributes, "id"),
    name: extractAttribute(attributes, "name"),
    // Normalized once, here, so the chip, its resolution key and the domain
    // sent to the API all read the same bare host.
    domain: bareDomainOf(extractAttribute(attributes, "domain")) ?? "",
    siren: extractAttribute(attributes, "siren"),
    siret: extractAttribute(attributes, "siret"),
    linkedin: extractAttribute(attributes, "linkedin"),
    city: extractAttribute(attributes, "city"),
    address: extractAttribute(attributes, "address"),
    activity: extractAttribute(attributes, "activity"),
    source: extractAttribute(attributes, "source"),
    brief: extractAttribute(attributes, "brief"),
  };
}

/**
 * Replaces complete `[@shaarp/company ...]` markers with `<company>` elements
 * and drops a trailing incomplete marker. Any other text is left untouched.
 */
export function transformCompanyMarkers(text: string): string {
  const withoutTrailingPartial = text.replace(
    TRAILING_INCOMPLETE_MARKER_REGEX,
    "",
  );

  return withoutTrailingPartial.replace(
    COMPANY_MARKER_REGEX,
    (_fullMatch, rawAttributes: string) => {
      const marker = parseMarkerAttributes(rawAttributes);

      // Without a name there is no label to show — drop the marker entirely
      // rather than leak raw syntax or render an empty chip.
      if (!marker.name) return "";

      const safeName = escapeHtml(marker.name);
      const elementAttributes = ELEMENT_ATTRIBUTES.filter(
        (attribute) => marker[attribute],
      )
        .map((attribute) => ` ${attribute}="${escapeHtml(marker[attribute])}"`)
        .join("");
      return `<company${elementAttributes} name="${safeName}">${safeName}</company>`;
    },
  );
}

/**
 * Flattens complete `[@shaarp/company ...]` markers down to their plain-text
 * label (the `name`), dropping nameless and trailing incomplete markers.
 *
 * Use this wherever assistant text is consumed as *plain text* instead of being
 * rendered as markdown — e.g. an email body shown in a textarea, then copied,
 * sent or downloaded as a `.md`. There the `<company>` chip can't render, so the
 * raw marker (including the internal company id) would otherwise leak verbatim.
 */
export function stripCompanyMarkers(text: string): string {
  const withoutTrailingPartial = text.replace(
    TRAILING_INCOMPLETE_MARKER_REGEX,
    "",
  );

  return withoutTrailingPartial.replace(
    COMPANY_MARKER_REGEX,
    (_fullMatch, rawAttributes: string) =>
      parseMarkerAttributes(rawAttributes).name,
  );
}
